import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import SearchAPI from 'apis/SearchAPI';

// Define query keys for search
export const searchKeys = {
  all: ['search'],
  project: (projectId) => [...searchKeys.all, 'project', projectId],
  query: (projectId, searchTerm) => [...searchKeys.project(projectId), searchTerm],
  lastSearch: (projectId) => [...searchKeys.project(projectId), 'lastSearch'],
};

/**
 * Hook to manage persisted search state for a project
 * @param {string|number} projectId - The ID of the project
 * @returns {Object} Object containing search state and setters
 */
export function useProjectSearchState(projectId) {
  const queryClient = useQueryClient();
  
  // Initialize state with cached value
  const [searchInput, setSearchInput] = useState(() => {
    const cached = queryClient.getQueryData(searchKeys.lastSearch(projectId));
    return cached || '';
  });
  
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState(() => {
    const cached = queryClient.getQueryData(searchKeys.lastSearch(projectId));
    return cached || '';
  });
  
  // Persist search term when it changes
  useEffect(() => {
    if (submittedSearchTerm) {
      queryClient.setQueryData(searchKeys.lastSearch(projectId), submittedSearchTerm);
      // Also update the input to match for consistency
      queryClient.setQueryData([...searchKeys.project(projectId), 'lastInput'], searchInput);
    }
  }, [submittedSearchTerm, searchInput, projectId, queryClient]);
  
  // Update state when projectId changes
  useEffect(() => {
    const cached = queryClient.getQueryData(searchKeys.lastSearch(projectId));
    const lastTerm = cached || '';
    setSearchInput(lastTerm);
    setSubmittedSearchTerm(lastTerm);
  }, [projectId, queryClient]);
  
  return {
    searchInput,
    setSearchInput,
    submittedSearchTerm,
    setSubmittedSearchTerm,
  };
}

/**
 * Hook to search transcripts within a project
 * @param {string|number} projectId - The ID of the project to search in
 * @param {string} searchTerm - The search term/query
 * @returns {Object} The query result containing search results, loading state, and error
 */
export function useTranscriptSearch(projectId, searchTerm) {
  return useQuery({
    queryKey: searchKeys.query(projectId, searchTerm),
    queryFn: async () => {
      if (!searchTerm || !searchTerm.trim()) {
        return {
          results: [],
          total: 0,
          searchTerm: '',
        };
      }
      
      const response = await SearchAPI.searchTranscripts(projectId, searchTerm);
      // Map the server response structure - server returns 'hits' not 'results'
      return {
        results: response.data.hits || [],
        total: response.data.total || 0,
        searchTerm: searchTerm,
      };
    },
    enabled: !!projectId && !!searchTerm && searchTerm.trim().length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    placeholderData: keepPreviousData, // Keep showing previous results while fetching new ones
  });
}